import {
  faLocationDot,
  faMagnifyingGlass,
  faSpinnerThird,
  faXmark,
} from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { parseAsFloat, parseAsString, useQueryState } from 'nuqs';
import { type CSSProperties, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { GeocodingSuggestion } from '@/api/mapbox';
import { useGeocoding } from '@/hooks/useGeocoding';
import { twx } from '@/utils/tailwind';

export interface SearchBarProps {
  className?: string;
  disabled?: boolean;
  onLocationSelected?: (name: string, lat: number, lng: number) => void;
}

export const SearchBar = ({ className, disabled = false, onLocationSelected }: SearchBarProps) => {
  const [locationName, setLocationName] = useQueryState('q', parseAsString.withDefault(''));
  const [, setLat] = useQueryState('lat', parseAsFloat);
  const [, setLng] = useQueryState('lng', parseAsFloat);

  const [inputValue, setInputValue] = useState(locationName);
  const [isTyping, setIsTyping] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const inputRef = useRef<HTMLInputElement>(null);
  const pillRef = useRef<HTMLDivElement>(null);
  // Anchored dropdown position. The SearchBar lives inside a frosted
  // chip wrapper with `overflow-hidden` (for crisp rounded corners), so
  // a regular absolutely-positioned dropdown is clipped out of view.
  // Portal-to-body + viewport-fixed coords lets it escape the clip.
  const [dropdownStyle, setDropdownStyle] = useState<CSSProperties | null>(null);

  const { suggestions, isLoading } = useGeocoding(isTyping ? inputValue : '');
  const showDropdown = isFocused && isTyping && (suggestions.length > 0 || isLoading);
  const hasLocation = locationName.length > 0 && !isTyping;

  useEffect(() => {
    if (!showDropdown) {
      setDropdownStyle(null);
      return;
    }
    const measure = () => {
      const rect = pillRef.current?.getBoundingClientRect();
      if (!rect) return;
      setDropdownStyle({ top: rect.bottom + 6, left: rect.left, width: rect.width });
    };
    measure();
    let pending = false;
    const onResize = () => {
      if (pending) return;
      pending = true;
      window.requestAnimationFrame(() => {
        pending = false;
        measure();
      });
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [showDropdown]);

  useEffect(() => {
    if (!isTyping) setInputValue(locationName);
  }, [locationName, isTyping]);

  const handleSelect = (suggestion: GeocodingSuggestion) => {
    setInputValue(suggestion.placeName);
    setIsTyping(false);
    setActiveIndex(-1);
    void setLocationName(suggestion.placeName);
    void setLat(suggestion.lat);
    void setLng(suggestion.lng);
    inputRef.current?.blur();
    onLocationSelected?.(suggestion.placeName, suggestion.lat, suggestion.lng);
  };

  const handleClear = () => {
    setInputValue('');
    setIsTyping(false);
    setActiveIndex(-1);
    void setLocationName('');
    void setLat(null);
    void setLng(null);
    inputRef.current?.focus();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    setIsTyping(true);
    setActiveIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const target = activeIndex >= 0 ? suggestions[activeIndex] : suggestions[0];
      if (target) handleSelect(target);
    } else if (e.key === 'Escape') {
      setIsTyping(false);
      setInputValue(locationName);
      setActiveIndex(-1);
      inputRef.current?.blur();
    }
  };

  const handleFocus = () => {
    if (disabled) return;
    setIsFocused(true);
    if (hasLocation) {
      setInputValue('');
      setIsTyping(true);
    }
  };

  const handleBlur = () => {
    setTimeout(() => {
      setIsFocused(false);
      if (isTyping) {
        setIsTyping(false);
        setInputValue(locationName);
      }
    }, 150);
  };

  return (
    <div className={twx('relative w-full', className)}>
      <div
        ref={pillRef}
        // No border — the surrounding header chip provides the visual frame.
        // Focus state uses a soft ring instead so the input still telegraphs
        // when it's active. The loupe is absolutely positioned (rather than a
        // flex sibling) so the input owns its own left padding — that way
        // glyphs with leftward hairlines (the W in "Where to next??") never
        // crowd the icon or the pill's rounded corner.
        className={twx(
          'relative flex items-center pr-3 py-2 min-h-10 bg-white rounded-lg! transition-all duration-150',
          disabled
            ? 'opacity-60 cursor-not-allowed'
            : isFocused
              ? 'ring-2 ring-secondary-emphasized/20'
              : '',
        )}
      >
        {/* The loupe stays — instead of swapping to a spinner while
            loading, we wiggle the magnifying glass so it feels like it's
            actively "hunting" for a place. */}
        <FontAwesomeIcon
          icon={faMagnifyingGlass}
          className={twx(
            'absolute left-4 top-1/2 -translate-y-1/2 text-content-subtle pointer-events-none',
            isLoading && 'motion-safe:animate-kawaii-wiggle',
          )}
          style={{ width: 14, height: 14 }}
        />

        <input
          ref={inputRef}
          type="text"
          value={hasLocation && !isFocused ? locationName : inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          readOnly={disabled}
          tabIndex={disabled ? -1 : undefined}
          placeholder="Where to next??"
          // pl-10 clears the absolute-positioned magnifier (left-4 + 14 + 4px buffer).
          className={twx(
            'flex-1 bg-transparent text-sm text-content-text placeholder:text-content-subtle outline-none min-w-0 pl-10',
            disabled && 'cursor-not-allowed select-none',
          )}
        />

        {hasLocation && !disabled && (
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              handleClear();
            }}
            className="shrink-0 w-6 h-6 flex items-center justify-center rounded-full text-content-subtle hover:text-content-text hover:bg-content-focal transition-colors motion-safe:active:scale-90"
            aria-label="Clear location"
            title="Clear"
          >
            <FontAwesomeIcon icon={faXmark} style={{ width: 12, height: 12 }} />
          </button>
        )}
      </div>

      {showDropdown &&
        dropdownStyle &&
        createPortal(
          <div
            style={dropdownStyle}
            className="fixed bg-white border border-content-border rounded-xl shadow-lg z-menu overflow-hidden max-h-[60vh] overflow-y-auto"
          >
            {isLoading && suggestions.length === 0 && (
              <div className="px-4 py-3 text-sm text-content-subtle flex items-center gap-2">
                <FontAwesomeIcon
                  icon={faSpinnerThird}
                  spin={true}
                  style={{ width: 14, height: 14 }}
                />
                Searching…
              </div>
            )}
            {suggestions.map((suggestion, index) => {
              const isActive = index === activeIndex;
              return (
                <button
                  type="button"
                  key={suggestion.id}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSelect(suggestion);
                  }}
                  // The active item (arrow-keyed) gets a left accent bar
                  // + slightly stronger bg + tinted location icon. The
                  // accent bar makes "which one will Enter pick?"
                  // instant — bg-only highlight was easy to miss when
                  // scanning a tall list of suggestions.
                  className={twx(
                    'w-full flex items-start gap-3 pl-4 pr-4 py-3 text-left transition-colors relative',
                    isActive
                      ? 'bg-secondary-subtle text-secondary-emphasized'
                      : 'hover:bg-content-focal/60',
                    index > 0 && 'border-t border-content-divider',
                  )}
                >
                  {isActive && (
                    <span
                      aria-hidden
                      className="absolute left-0 top-0 bottom-0 w-0.5 bg-secondary-emphasized"
                    />
                  )}
                  <FontAwesomeIcon
                    icon={faLocationDot}
                    className={twx(
                      'mt-0.5 shrink-0 transition-colors',
                      isActive ? 'text-secondary-emphasized' : 'text-content-subtle',
                    )}
                    style={{ width: 14, height: 14 }}
                  />
                  <span
                    className={twx(
                      'text-sm leading-snug',
                      isActive ? 'text-content-text font-medium' : 'text-content-text',
                    )}
                  >
                    {suggestion.placeName}
                  </span>
                </button>
              );
            })}
          </div>,
          document.body,
        )}
    </div>
  );
};
